import React, { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
// Use core for group and contacts operations
import { listContactsCore, createGroupCore, updateGroupCore, addContactToGroupCore, inviteToGroupCore } from '@gv/core';
import { useGroupContext } from '../lib/groups/context';
import { Plus, ArrowLeft, X as XIcon } from 'lucide-react';

type Props = {
    user: any;
    onBack: () => void;
    onCreated: () => void;
};

type Contact = {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    email?: string | null;
    profile_image_url?: string | null;
};

export default function CreateGroupPage({ user, onBack, onCreated }: Props) {
    const { reload } = useGroupContext();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [startDate, setStartDate] = useState<string>(() => new Date().toISOString().slice(0, 10));
    // Removed imageUrl/uploading per request
    const [saving, setSaving] = useState(false);
    const [contacts, setContacts] = useState<Contact[]>([]);
    const [selectedIds, setSelectedIds] = useState<string[]>([]);
    const [search, setSearch] = useState('');
    const [inviteEmail, setInviteEmail] = useState('');
    const [inviteEmails, setInviteEmails] = useState<string[]>([]);
    const [inviteSelectedWithEmail, setInviteSelectedWithEmail] = useState(false);
    // Removed zoom state

    useEffect(() => {
        let isMounted = true;
        (async () => {
            try {
                const rows = await listContactsCore(supabase as any, { includeArchived: false, limit: 500 });
                if (isMounted) setContacts((rows as any) || []);
            } catch (e) {
                console.error('Failed to load contacts', e);
            }
        })();
        return () => { isMounted = false; };
    }, [user?.id]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return contacts;
        return contacts.filter(c =>
            `${c.first_name || ''} ${c.last_name || ''}`.toLowerCase().includes(q) ||
            (c.email || '').toLowerCase().includes(q)
        );
    }, [contacts, search]);

    const toggleSelect = (id: string) => {
        setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
    };

    // Image picking removed

    const addInviteEmail = () => {
        const email = inviteEmail.trim().toLowerCase();
        if (!email) return;
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
            alert('Enter a valid email');
            return;
        }
        if (!inviteEmails.includes(email)) setInviteEmails([...inviteEmails, email]);
        setInviteEmail('');
    };

    const removeInvite = (email: string) => setInviteEmails(inviteEmails.filter(e => e !== email));

    const handleCreate = async () => {
        if (!name.trim()) {
            alert('Name is required');
            return;
        }
        setSaving(true);
        try {
            // Create the base group via core, then update optional fields
            const g = await createGroupCore(supabase as any, name.trim());
            try { await updateGroupCore(supabase as any, g.id, { description: description || null, start_date: startDate || null } as any); } catch (e) { console.warn('Optional fields update skipped', e); }

            // Add selected contacts
            for (const id of selectedIds) {
                try { await addContactToGroupCore(supabase as any, id, g.id); } catch (e) { console.warn('Add member failed', e); }
            }

            // Create invites entered manually
            for (const email of inviteEmails) {
                try { await inviteToGroupCore(supabase as any, g.id, email); } catch (e) { console.warn('Invite failed', e); }
            }

            // Optionally invite selected contacts if they have an email
            if (inviteSelectedWithEmail) {
                const emails = contacts.filter(c => selectedIds.includes(c.id) && c.email).map(c => (c.email as string).toLowerCase());
                const unique = Array.from(new Set(emails));
                for (const email of unique) {
                    try { await inviteToGroupCore(supabase as any, g.id, email); } catch (e) { console.warn('Invite selected contact failed', e); }
                }
            }

            await reload();
            onCreated();
        } catch (e) {
            console.error(e);
            alert('Failed to create group');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="p-4 md:p-6 lg:p-8 text-white">
            <div className="max-w-5xl mx-auto">
                <div className="flex items-center justify-between mb-4 sm:mb-6 flex-wrap gap-3">
                    <button onClick={onBack} className="w-9 h-9 bg-gray-800 border border-gray-700 rounded-lg flex items-center justify-center text-white hover:bg-gray-700" aria-label="Back">
                        <ArrowLeft className="w-5 h-5" />
                    </button>
                    <h2 className="text-xl md:text-2xl font-light tracking-wide text-center flex-1">New Group</h2>
                    <button onClick={handleCreate} disabled={saving} className="px-4 py-2 bg-white text-black font-medium">
                        {saving ? 'Creating...' : 'Create Group'}
                    </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
                    {/* Left: Basics only (image removed) */}
                    <div className="md:col-span-1 border border-gray-800 p-3 sm:p-4 bg-black">
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Group Name</label>
                                <input value={name} onChange={e => setName(e.target.value)} placeholder="e.g., Family, VIP Members"
                                    className="w-full bg-gray-900 border border-gray-800 px-3 py-2" />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Start Date</label>
                                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)}
                                    className="w-full bg-gray-900 border border-gray-800 px-3 py-2" />
                            </div>
                            <div>
                                <label className="block text-xs text-gray-400 mb-1">Description</label>
                                <textarea value={description} onChange={e => setDescription(e.target.value)} rows={4}
                                    className="w-full bg-gray-900 border border-gray-800 px-3 py-2" placeholder="What’s this group for?" />
                            </div>
                        </div>
                    </div>

                    {/* Right: Members and Invites */}
                    <div className="md:col-span-2 space-y-6">
                        {/* Invite by email */}
                        <div className="border border-gray-800 p-3 sm:p-4 bg-black">
                            <div className="flex flex-col sm:flex-row sm:items-end gap-2">
                                <div className="flex-1">
                                    <label className="block text-xs text-gray-400 mb-1">Invite by Email</label>
                                    <input value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} placeholder="name@example.com"
                                        className="w-full bg-gray-900 border border-gray-800 px-3 py-2" />
                                </div>
                                <button onClick={addInviteEmail} className="px-3 py-2 border border-gray-800 bg-gray-900 hover:bg-gray-800 w-full sm:w-auto">Add</button>
                            </div>
                            <label className="mt-3 flex items-center gap-2 text-sm text-gray-300">
                                <input type="checkbox" className="w-4 h-4" checked={inviteSelectedWithEmail} onChange={e => setInviteSelectedWithEmail(e.target.checked)} />
                                Also invite selected contacts who have an email
                            </label>
                            {inviteEmails.length > 0 && (
                                <div className="mt-3 flex flex-wrap gap-2">
                                    {inviteEmails.map(email => (
                                        <span key={email} className="inline-flex items-center gap-2 px-2 py-1 text-sm bg-gray-900 border border-gray-800">
                                            {email}
                                            <button onClick={() => removeInvite(email)} className="text-gray-400 hover:text-white">×</button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Select existing contacts */}
                        <div className="border border-gray-800 bg-black">
                            <div className="p-3 sm:p-4 flex items-center justify-between gap-3 flex-wrap">
                                <div className="font-medium">Add Existing Contacts</div>
                                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search contacts"
                                    className="w-full md:w-72 bg-gray-900 border border-gray-800 px-3 py-2" />
                            </div>
                            <div className="max-h-64 sm:max-h-80 overflow-auto divide-y divide-gray-900">
                                {filtered.map(c => {
                                    const selected = selectedIds.includes(c.id);
                                    return (
                                        <button key={c.id} onClick={() => toggleSelect(c.id)} className={`w-full text-left p-3 hover:bg-gray-900 flex items-center gap-3 ${selected ? 'bg-gray-900' : ''}`}>
                                            <div className="w-8 h-8 bg-gray-800 rounded-full overflow-hidden flex-shrink-0">
                                                {c.profile_image_url ? <img src={c.profile_image_url} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full" />}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="truncate">{[c.first_name, c.last_name].filter(Boolean).join(' ') || 'Unnamed'}</div>
                                                <div className="text-xs text-gray-400 truncate">{c.email || ''}</div>
                                            </div>
                                            <div className={`w-4 h-4 rounded border ${selected ? 'bg-white border-white' : 'border-gray-600'}`} />
                                        </button>
                                    );
                                })}
                                {filtered.length === 0 && (
                                    <div className="p-4 text-gray-400">No contacts found</div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
